import type { CampaignOwner } from 'c-sdk';

/**
 * The per-owner audience row on the campaign schedule page. The API side is
 * `CampaignOwnerUpdateRequest` (uniliveapi): `audience_type` in `CampaignAudienceType`
 * (default | audience | logs | impressions_per_play) and `last_played_date_by` in default | media.
 *
 * The "impressions per play" option is shown by its translated label, and the form state holds
 * that label rather than the API value, so the label is passed in.
 */

export type LastPlayedDateBy = 'log' | 'media';

/** What a row falls back to when the owner has never had "last played" set. */
export const LAST_PLAYED_FALLBACK: LastPlayedDateBy = 'log';

export type OwnerAudienceFormRow = {
    audience_type: string;
    value: string | number;
    last_played_date_by: string;
};

export type CampaignOwnerAudiencePayload = {
    audience_type: string;
    last_played_date_by?: LastPlayedDateBy;
    impressions_per_play?: number;
    audience_version_id?: number;
};

export const initialOwnerAudienceRow = (
    owner: CampaignOwner,
    impressionsPerPlayLabel: string,
): OwnerAudienceFormRow => ({
    audience_type:
        owner.audience_type === 'impressions_per_play'
            ? impressionsPerPlayLabel
            : owner.audience_type ?? 'default',
    value:
        owner.audience_type === 'impressions_per_play'
            ? Number(owner.impressions_per_play)
            : owner.audience_version_id ?? '',
    last_played_date_by: owner.last_played_date_by ?? LAST_PLAYED_FALLBACK,
});

/** The secondary value a row starts with after its audience type is switched. */
export const valueForAudienceType = (
    owner: CampaignOwner | undefined,
    newType: string,
    impressionsPerPlayLabel: string,
): string | number => {
    if (newType === 'audience') return owner?.audience_version_id ?? '';
    if (newType === impressionsPerPlayLabel)
        return owner?.impressions_per_play ? Number(owner.impressions_per_play) : '';
    return '';
};

export const buildCampaignOwnerAudiencePayload = (
    audienceType: string,
    value: string | number | undefined,
    lastPlayed: LastPlayedDateBy | undefined,
    impressionsPerPlayLabel: string,
): CampaignOwnerAudiencePayload => {
    const resolvedType =
        audienceType === impressionsPerPlayLabel ? 'impressions_per_play' : audienceType;

    const payload: CampaignOwnerAudiencePayload = { audience_type: resolvedType };

    if (lastPlayed) payload.last_played_date_by = lastPlayed;
    if (resolvedType === 'audience') payload.audience_version_id = Number(value);
    if (resolvedType === 'impressions_per_play') payload.impressions_per_play = Number(value);

    return payload;
};
